import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Stack,
  TextField,
  Typography,
  FormLabel,
  Tooltip,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  golfTooltipHeaders,
  originsTooltip,
} from "../../../constants/TooltipInformation";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const DEFAULT_BLOCK_COLOR = "#FFFFFF";
const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{6})$/;

const normalizeAllocationBlockColor = (value: unknown): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  const raw = String(value).trim();
  if (!raw) {
    return null;
  }
  if (HEX_COLOR_PATTERN.test(raw)) {
    return raw.toUpperCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(raw)) {
    return `#${raw.toUpperCase()}`;
  }
  return null;
};

const isDefaultBlockColor = (color: string | null): boolean =>
  color === DEFAULT_BLOCK_COLOR.toUpperCase();

const isDuplicateAllocationColor = (
  list: any[],
  color: string,
  excludeId?: string | null,
) => {
  const normalized = normalizeAllocationBlockColor(color);
  if (!normalized || isDefaultBlockColor(normalized)) {
    return false;
  }
  return (list ?? []).some((item) => {
    if (excludeId != null && String(item.id) === String(excludeId)) {
      return false;
    }
    const existingColor = normalizeAllocationBlockColor(
      item[DNDFieldsEnum.COLOROPTIONS],
    );
    if (!existingColor || isDefaultBlockColor(existingColor)) {
      return false;
    }
    return existingColor === normalized;
  });
};

const GolfAllocationBlock = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [blockCode, setBlockCode] = useState<string | null>(null);
  const [blockName, setBlockName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [showOnTeeSheet, setShowOnTeeSheet] = useState<boolean>(true);
  const [closed, setClosed] = useState<boolean>(false);
  const [allocationBlock, setAllocationBlock] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [nameError2, setNameError2] = useState<boolean>(false);
  const [colorError, setColorError] = useState<boolean>(false);
  const [blockColor, setBlockColor] = useState<string>(DEFAULT_BLOCK_COLOR);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  useEffect(() => {
    data(allocationBlock);
    editClicked &&
      setSelectedId(checkData(allocationBlock, selectedId) ? selectedId : null);
  }, [allocationBlock]);

  useEffect(() => {
    fieldValues && setAllocationBlock(fieldValues);
  }, []);

  const addAllocationBlock = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        allocationBlock ?? [],
        DNDFieldsEnum.ALLOCATIONBLOCKCODE,
        blockCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (isDuplicateAllocationColor(allocationBlock ?? [], blockColor)) {
      setColorError(true);
      setAlertMessage(
        "This color is already used by another allocation block. The default color (#FFFFFF) may be reused.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setAllocationBlock((prev: any) => {
      return updateData(
        prev?.length
          ? [
              ...prev,
              {
                id: (prev?.length ?? 0) + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editAllocationBlock(null);
    setEditClicked(false);
    setActive(true);
    setShowOnTeeSheet(true);
    setClosed(false);
    setBlockColor(DEFAULT_BLOCK_COLOR);
  };

  const editAllocationBlock = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setBlockCode(list?.["Allocation Block Code"]);
    setBlockName(list?.["Allocation Block Name"]);
    setActive(list?.Active ?? true);
    setShowOnTeeSheet(list?.["Show on Tee Sheet"] ?? true);
    setClosed(list?.["Closed ?"] ?? false);
    setBlockColor(
      normalizeAllocationBlockColor(list?.[DNDFieldsEnum.COLOROPTIONS]) ??
        DEFAULT_BLOCK_COLOR,
    );
    setColorError(false);
  };
  const normalizedBlockColor =
    normalizeAllocationBlockColor(blockColor) ?? DEFAULT_BLOCK_COLOR;
  const hasInvalidColorFormat =
    String(blockColor ?? "").trim() !== "" &&
    !normalizeAllocationBlockColor(blockColor);
  const hasDuplicateNonDefaultColor = isDuplicateAllocationColor(
    allocationBlock ?? [],
    blockColor,
    selectedId,
  );
  const colorHelperText = hasInvalidColorFormat
    ? "Enter a valid hex color (e.g. #FFFFFF) or leave the default."
    : hasDuplicateNonDefaultColor
      ? "This color is already used by another allocation block."
      : "";
  const payload = {
    "Allocation Block Code": blockCode,
    "Allocation Block Name": blockName,
    "Days out": 0,
    Active: active,
    [DNDFieldsEnum.COLOROPTIONS]: normalizedBlockColor,
    "Switch To": "",
    "Show on Tee Sheet": showOnTeeSheet,
    "Closed ?": closed,
  };
  const validateInput = () => {
    if (!blockCode) {
      setAlertMessage("Block code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    if (!blockName) {
      setAlertMessage("Block name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    setNameError(false);
    if (
      !validateString(
        blockCode,
        validationLength.blockCode,
        validationType.CODE,
      )
    ) {
      setNameError(true);
      return false;
    }

    setNameError2(false);
    if (
      !validateString(
        blockName,
        validationLength.blockName,
        validationType.CODE,
      )
    ) {
      setNameError2(true);
      return false;
    }

    setColorError(false);
    const trimmedColor = String(blockColor ?? "").trim();
    if (trimmedColor && !normalizeAllocationBlockColor(blockColor)) {
      setColorError(true);
      setAlertMessage("Enter a valid hex color (e.g. #FFFFFF) or leave the default.");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  // useEffect(() => {
  //   validateInput();
  // }, [blockCode, blockName]);

  const updateAllocationBlock = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        (allocationBlock ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.ALLOCATIONBLOCKCODE,
        blockCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (
      isDuplicateAllocationColor(allocationBlock ?? [], blockColor, selectedId)
    ) {
      setColorError(true);
      setAlertMessage(
        "This color is already used by another allocation block. The default color (#FFFFFF) may be reused.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setNameError2(false);
    setAllocationBlock((prev: any) => {
      return (prev ?? []).map((item: any) => {
        if (item.id === selectedId) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editAllocationBlock(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${GolfPropertyEnum.allocationBlocks} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteAllocationBLock = (id: number) => {
    setAllocationBlock(deleteItem(allocationBlock ?? [], id));
  };

  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={textStyle}>
            Allocation Blocks
            <Tooltip
              title={tooltipComponent(golfTooltipHeaders.allocationBlockHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editAllocationBlock(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton
                click={() => {
                  editAllocationBlock(lastUpdatedRecord);
                }}
              />
            )}
          </Stack>
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Allocation Block Code
            <Tooltip
              title={tooltipComponent(golfTooltipHeaders.allocationBlockCode)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>{" "}
          <TextField
            id="allocationBlockCode"
            variant="outlined"
            placeholder="Allocation Block Code"
            helperText={nameError ? validationMessages.blockCode : ""}
            error={nameError}
            value={blockCode ?? ""}
            onChange={(e: any) => {
              setBlockCode(e.target.value);
            }}
          />
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Allocation Block Name
            <Tooltip
              title={tooltipComponent(golfTooltipHeaders.allocationBlockName)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>{" "}
          <TextField
            id="allocationBlockName"
            variant="outlined"
            placeholder="Allocation Block Name"
            helperText={nameError2 ? validationMessages.blockName : ""}
            error={nameError2}
            value={blockName ?? ""}
            onChange={(e: any) => {
              setBlockName(e.target.value);
            }}
          />
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Color Options
            <Tooltip
              title={tooltipComponent(golfTooltipHeaders.allocationBlockColor)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <Stack
            className="form-two-col-row"
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <input
              type="color"
              aria-label="Allocation block color"
              value={
                HEX_COLOR_PATTERN.test(blockColor)
                  ? blockColor.toLowerCase()
                  : DEFAULT_BLOCK_COLOR.toLowerCase()
              }
              onChange={(e) => {
                setBlockColor(e.target.value.toUpperCase());
                setColorError(false);
              }}
              style={{
                width: 48,
                height: 40,
                padding: 0,
                border: "1px solid #ccc",
                borderRadius: 4,
                cursor: "pointer",
                background: "transparent",
              }}
            />
            <TextField
              id="allocationBlockColor"
              variant="outlined"
              placeholder="#FFFFFF"
              size="small"
              helperText={colorHelperText}
              error={
                colorError || hasInvalidColorFormat || hasDuplicateNonDefaultColor
              }
              value={blockColor}
              onChange={(e) => {
                setBlockColor(e.target.value);
                setColorError(false);
              }}
              sx={{ maxWidth: 160 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            The color is not mandatory and it can be changed later in the Golf
            environment.
          </Typography>
        </Stack>
        <Stack direction="row" gap="2px">
          <FormControlLabel
            control={
              <Switch checked={active} onChange={() => setActive(!active)} />
            }
            label="Active"
          />
          <Tooltip title={tooltipComponent(originsTooltip.active)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack>
        <Stack direction="row" gap="2px">
          <FormControlLabel
            control={
              <Switch
                checked={showOnTeeSheet}
                onChange={() => setShowOnTeeSheet(!showOnTeeSheet)}
              />
            }
            label="Show On Tee Sheet?"
          />
          <Tooltip title={tooltipComponent(golfTooltipHeaders.showOnTeeSheet)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack>
        <Stack direction="row" gap="2px">
          <FormControlLabel
            control={
              <Switch checked={closed} onChange={() => setClosed(!closed)} />
            }
            label="Closed?"
          />
          <Tooltip title={tooltipComponent(golfTooltipHeaders.closed)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() =>
              selectedId ? updateAllocationBlock() : addAllocationBlock()
            }
            id="addNewPaymentInformation"
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
            Allocation Block
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Allocation Block Available</Typography>
          <Stack direction="column" gap="10px">
            {allocationBlock ? (
              <DraggableItemList
                itemData={allocationBlock}
                onOrderUpdate={(order) => setAllocationBlock(order)}
                onEdit={(info) => editAllocationBlock(info)}
                onDelete={(id) => deleteAllocationBLock(id)}
                code={DNDFieldsEnum.ALLOCATIONBLOCKCODE}
                name={DNDFieldsEnum.ALLOCATIONBLOCKNAME}
              />
            ) : (
              <Typography variant="body2">
                No Allocation Block Information Available
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};
export default GolfAllocationBlock;
